import { create } from 'zustand';
import appConfig from '../config/app-config.json';
import { applyFacets } from '../helpers/facets';
import { deriveAttributes } from '../helpers/deriveAttributes';
import { sendEmailRequestMap } from '../helpers/messagesMappers';
import { batchReduce, getCategories, importImages, searchParse } from '../helpers/utls';
import TelmeApi from '../services/TelmeApi';
import {
  AttributeDef,
  CategoryDef,
  FacetSelection,
  Filters,
  HttpRequestError,
  Product,
  Products,
  SendEmailForm,
  SortOption,
  TelmeImage,
} from '../types';
import { useErrorStore } from './useErrorStore';

let cachedProducts: Product[] = [];
let cachedDefs: AttributeDef[] = [];

const defaultSort: SortOption = {
  description: 'title',
  sort: (a: Product, b: Product) => (a.title < b.title ? -1 : 1),
};

const displayImages: TelmeImage[] = importImages(
  import.meta.glob<string>('/src/img/display/*.{webp,svg}', {
    eager: true,
    query: '?url',
    import: 'default',
  }),
);

const batch = (p: Product[]) => batchReduce(p, appConfig.products.productsPerPage);

const applyFilters = (searchBy: string, facets: FacetSelection, sort: SortOption): Products[] =>
  batch(
    applyFacets(cachedProducts, facets, cachedDefs)
      .filter(
        p =>
          searchBy === '' ||
          searchParse(p.title).includes(searchParse(searchBy)) ||
          searchParse(p.salesId).includes(searchParse(searchBy)),
      )
      .sort(sort.sort),
  );

interface ProductsState {
  loading: boolean;
  allProductsList: Product[];
  productsData: Products[];
  displayImages: TelmeImage[];
  categories: string[];
  attributeDefs: AttributeDef[];
  categoryDefs: CategoryDef[];
  filters: Filters;
  fetchProducts: () => Promise<void>;
  setSearchBy: (searchBy: string) => void;
  setFacets: (facets: FacetSelection) => void;
  setSortBy: (sortBy: SortOption) => void;
  sendEmail: (data: SendEmailForm) => Promise<void>;
}

export const useProductsStore = create<ProductsState>()((set, get) => ({
  loading: true,
  allProductsList: [],
  productsData: [],
  displayImages,
  categories: [],
  attributeDefs: [],
  categoryDefs: [],
  filters: { sortBy: defaultSort, searchBy: '', facets: {} },

  fetchProducts: async () => {
    try {
      const [productsRes, attributesRes, categoriesRes] = await Promise.all([
        fetch('/products.json'),
        fetch('/attributes.json'),
        fetch('/categories.json'),
      ]);
      if (!productsRes.ok) throw new Error(`Failed to load products: ${productsRes.status}`);
      if (!attributesRes.ok) throw new Error(`Failed to load attributes: ${attributesRes.status}`);
      if (!categoriesRes.ok) throw new Error(`Failed to load categories: ${categoriesRes.status}`);
      const rawProducts = (await productsRes.json()) as Product[];
      const defs = (await attributesRes.json()) as AttributeDef[];
      const categoryDefs = (await categoriesRes.json()) as CategoryDef[];
      const products = rawProducts.map(p => ({
        ...p,
        attributes: deriveAttributes(p.features, defs),
      }));
      cachedProducts = products;
      cachedDefs = defs;
      set({
        loading: false,
        allProductsList: products,
        attributeDefs: defs,
        categoryDefs,
        productsData: batch(products.toSorted(defaultSort.sort)),
        categories: getCategories(batch(products)),
      });
    } catch (error) {
      useErrorStore.getState().setHttpError(error as HttpRequestError);
      set({ loading: false });
    }
  },

  setSearchBy: searchBy => {
    const { filters } = get();
    set({
      productsData: applyFilters(searchBy, filters.facets, filters.sortBy),
      filters: { ...filters, searchBy },
    });
  },
  setFacets: facets => {
    const { filters } = get();
    set({
      productsData: applyFilters(filters.searchBy, facets, filters.sortBy),
      filters: { ...filters, facets },
    });
  },
  setSortBy: sortBy => {
    const { filters } = get();
    set({
      productsData: applyFilters(filters.searchBy, filters.facets, sortBy),
      filters: { ...filters, sortBy },
    });
  },

  sendEmail: async (data: SendEmailForm) => {
    try {
      await TelmeApi.sendEmail(sendEmailRequestMap(data));
    } catch (error) {
      useErrorStore.getState().setHttpError(error as HttpRequestError);
      throw error;
    }
  },
}));
